import React, { useEffect, useState, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Spin } from 'antd';
import PropTypes from 'prop-types';
import moment from 'moment';
import Avatars from '../../components/Avatars';
import Table from '../../components/Table';
import TableHeader from '../../components/TableHeader';
import GlobalContext from '../../../main/utils/GlobalContext';

const columns = (locales) => [
  {
    title: locales.admin.project,
    dataIndex: 'name',
    key: 'name',
    render: (name) => {
      const displayName =
        name && name.length > 200 ? `${name.substring(0, 200)}...` : name || '';
      return (
        <p
          title={name || ''}
          className="overflow-hidden text-wrap"
          style={{ maxWidth: '150px' }}
        >
          {displayName}
        </p>
      );
    },
  },
  {
    title: locales.admin.teamMember,
    key: 'teamMembers',
    render: (row) => {
      const images = row.teamMembers.map((item) => {
        const fullName = `${item.firstName} ${item.surname}`;
        return {
          alt: fullName,
          url: item.image.icon.url,
          fullName,
        };
      });
      return <Avatars images={images} />;
    },
  },
  {
    title: locales.admin.class,
    dataIndex: 'classroomName',
    key: 'classroomName',
    render: (classroomName) => {
      const displayName =
        classroomName && classroomName.length > 200
          ? `${classroomName.substring(0, 200)}...`
          : classroomName || '';
      return (
        <p
          title={classroomName || ''}
          className="overflow-hidden text-wrap"
          style={{ maxWidth: '150px' }}
        >
          {displayName}
        </p>
      );
    },
  },
  {
    title: locales.admin.ideasCreated,
    dataIndex: 'ideasCreated',
    key: 'ideasCreated',
    render: (ideasCreated) => ideasCreated || 0,
  },
  {
    title: locales.admin.canvasProgress,
    dataIndex: 'canvasProgress',
    key: 'canvasProgress',
    render: (canvasProgress) => canvasProgress || '0%',
  },
  {
    title: locales.admin.testsCompleted,
    dataIndex: 'testsCreated',
    key: 'testsCreated',
    render: (testsCreated) => testsCreated || 0,
  },
  {
    title: locales.admin.activityTotal,
    dataIndex: 'sumTimeTracks',
    key: 'sumTimeTracks',
    render: (sumTimeTracks) => sumTimeTracks || 0,
  },
  {
    title: locales.admin.last30Days,
    dataIndex: 'sumLastTimeTracks',
    key: 'sumLastTimeTracks',
    render: (sumLastTimeTracks) => sumLastTimeTracks || 0,
  },
  {
    title: locales.admin.portfolioComments,
    dataIndex: 'commentsTotal',
    key: 'commentsTotal',
    render: (commentsTotal) => commentsTotal || 0,
  },
  {
    title: locales.admin.updated,
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    render: (updatedAt) => {
      moment.locale('en');
      return moment(new Date(updatedAt)).format('DD MMM YYYY');
    },
  },
];

const ProjectLists = ({
  byId,
  by,
  establishmentId,
  rowClick,
  rowClickable = false,
}) => {
  const { locales } = useContext(GlobalContext);
  const [search, setSearch] = useState(undefined);

  const {
    bmcs: { data, meta, loading },
    pagination: { pageSize: initPageSize },
  } = useSelector((state) => state);

  const {
    bmcs: { fetchBmcs, sortBmcs },
  } = useDispatch();

  useEffect(() => {
    fetchBmcs({ byId, by, search, limit: initPageSize, establishmentId });
  }, [byId, by, search, initPageSize, establishmentId]);

  return (
    <>
      <TableHeader
        title={locales.admin.projects}
        count={loading ? <Spin /> : meta?.count || 0}
        onSearch={(searchValue) => {
          setSearch(searchValue);
        }}
        searchLoading={loading}
      />
      <Table
        columns={columns(locales)}
        dataSource={data}
        loading={loading}
        total={meta?.count || 0}
        current={meta?.page || 1}
        onChange={(pagination) => {
          const { current, pageSize } = pagination;
          fetchBmcs({
            byId,
            by,
            establishmentId,
            search,
            page: current,
            limit: pageSize,
          });
        }}
        onRow={(record) => ({ onClick: () => rowClick(record) })}
        rowClickable={rowClickable}
        onSort={sortBmcs}
        meta={meta}
      />
    </>
  );
};

ProjectLists.propTypes = {
  byId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  by: PropTypes.oneOf(['users', 'classrooms', 'establishments']),
  rowClick: PropTypes.func,
  rowClickable: PropTypes.bool,
};
export default ProjectLists;
